import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2, UploadCloud, CheckCircle2, AlertTriangle } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import InventorySubNav from "@/components/inventory/InventorySubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function ImportInventory() {
  const [file, setFile] = useState(null);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sheetNames, setSheetNames] = useState(null);
  const [mappings, setMappings] = useState({});
  const [rows, setRows] = useState(null);
  const [loadingSheets, setLoadingSheets] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [summary, setSummary] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    api.get("/inventory/locations").then(({ data }) => setLocations(data));
    api.get("/inventory/categories").then(({ data }) => setCategories(data));
  }, []);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setSheetNames(null);
    setRows(null);
    setSummary(null);
    if (!f) return;
    setLoadingSheets(true);
    try {
      const formData = new FormData();
      formData.append("file", f);
      const { data } = await api.post("/inventory/import/sheet-names", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setSheetNames(data.sheet_names);
      const initial = {};
      data.sheet_names.forEach((s) => { initial[s] = { location_id: "", category_id: "", skip: false }; });
      setMappings(initial);
    } catch (err) {
      toast({ variant: "destructive", title: "Could not read workbook", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setLoadingSheets(false);
    }
  };

  const updateMapping = (sheet, field, value) => setMappings((prev) => ({ ...prev, [sheet]: { ...prev[sheet], [field]: value } }));

  const handleParse = async () => {
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const mappingArray = Object.entries(mappings).map(([sheet_name, m]) => ({ sheet_name, ...m }));
      formData.append("sheet_mappings", JSON.stringify(mappingArray));
      const { data } = await api.post("/inventory/import/parse", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(data.rows.map((r, idx) => ({ ...r, _key: idx })));
    } catch (err) {
      toast({ variant: "destructive", title: "Could not parse workbook", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (key, field, value) => setRows((prev) => prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)));

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const payloadRows = rows.map(({ _key, ...r }) => r);
      const { data } = await api.post("/inventory/import/confirm", { file_name: file?.name, rows: payloadRows });
      setSummary(data);
      toast({ title: "Import complete", description: `${data.items_created} new items, ${data.batches_created} batches created` });
    } catch (err) {
      toast({ variant: "destructive", title: "Import failed", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setConfirming(false);
    }
  };

  const mappedSheetsReady = sheetNames?.length > 0 && sheetNames.every((s) => mappings[s]?.skip || mappings[s]?.location_id);

  return (
    <div className="min-h-screen bg-amtm-bg">
      <StaffHeader />
      <div className="max-w-5xl mx-auto px-4 py-6 pb-24">
        <h1 className="font-display text-3xl text-amtm-text mb-2" data-testid="import-inventory-title">Import Workbook</h1>
        <InventorySubNav />

        <div className="bg-amtm-surface border-2 border-dashed border-amtm-teal/40 rounded-2xl p-8 flex flex-col items-center gap-3 mb-6">
          <UploadCloud className="h-10 w-10 text-amtm-teal" />
          <input type="file" accept=".xlsx" onChange={handleFileChange} data-testid="import-file-input" className="text-sm" />
          {loadingSheets && <Loader2 className="h-5 w-5 animate-spin text-amtm-teal" />}
        </div>

        {sheetNames && !rows && (
          <div className="space-y-3 mb-6" data-testid="import-sheet-mapping-list">
            <p className="text-sm text-amtm-textMuted">Map each sheet to a drawer and category, or skip it.</p>
            {sheetNames.map((sheet) => (
              <div key={sheet} className="flex items-center gap-3 flex-wrap bg-amtm-surface border border-amtm-border rounded-lg p-3" data-testid={`import-sheet-mapping-${sheet}`}>
                <p className="font-medium text-amtm-text w-40 truncate">{sheet}</p>
                <Select value={mappings[sheet]?.location_id} onValueChange={(v) => updateMapping(sheet, "location_id", v)}>
                  <SelectTrigger className="w-40" data-testid={`import-sheet-location-${sheet}`}><SelectValue placeholder="Drawer" /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={mappings[sheet]?.category_id} onValueChange={(v) => updateMapping(sheet, "category_id", v)}>
                  <SelectTrigger className="w-40" data-testid={`import-sheet-category-${sheet}`}><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm text-amtm-textMuted">
                  <Checkbox checked={mappings[sheet]?.skip} onCheckedChange={(v) => updateMapping(sheet, "skip", !!v)} data-testid={`import-sheet-skip-${sheet}`} />
                  Skip this sheet
                </label>
              </div>
            ))}
            <Button onClick={handleParse} disabled={!mappedSheetsReady || parsing} data-testid="import-parse-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">
              {parsing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Parse & Preview
            </Button>
          </div>
        )}

        {rows && !summary && (
          <div data-testid="import-preview-section">
            <p className="text-sm text-amtm-textMuted mb-3">{rows.length} rows parsed. Fix or uncheck rows with errors, then confirm. Nothing is saved yet.</p>
            <div className="bg-amtm-surface border border-amtm-border rounded-xl divide-y divide-amtm-border mb-4 max-h-[32rem] overflow-y-auto" data-testid="import-preview-rows">
              {rows.map((row) => (
                <div key={row._key} className={`p-3 flex items-start gap-3 flex-wrap ${row.errors?.length ? "bg-amtm-error/10" : row.warnings?.length ? "bg-amtm-warning/10" : ""}`} data-testid={`import-preview-row-${row._key}`}>
                  <Checkbox checked={row.include} onCheckedChange={(v) => updateRow(row._key, "include", !!v)} data-testid={`import-preview-include-${row._key}`} className="mt-2" />
                  <div className="flex-1 min-w-[180px]">
                    <Input value={row.name} onChange={(e) => updateRow(row._key, "name", e.target.value)} className="h-8 mb-1" data-testid={`import-preview-name-${row._key}`} />
                    <p className="text-xs text-amtm-textMuted">{row.sheet_name} · row {row.row_number}{row.matched_existing_item_id ? " · matches existing item" : " · new item"}</p>
                  </div>
                  <Input type="number" value={row.op_stock} onChange={(e) => updateRow(row._key, "op_stock", Number(e.target.value))} className="h-8 w-24" data-testid={`import-preview-opstock-${row._key}`} />
                  <div className="text-xs">
                    {row.errors?.map((e) => <p key={e} className="text-amtm-error flex items-center gap-1" data-testid={`import-preview-error-${row._key}`}><AlertTriangle className="h-3 w-3" />{e}</p>)}
                    {row.warnings?.map((w) => <p key={w} className="text-amtm-warning">{w}</p>)}
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={handleConfirm} disabled={confirming} data-testid="import-confirm-button" className="bg-amtm-teal text-white hover:bg-amtm-tealHover">
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirm Import
            </Button>
          </div>
        )}

        {summary && (
          <div>
            <div className="grid grid-cols-3 gap-4 mb-4" data-testid="import-summary">
              <div className="bg-amtm-success/10 border border-amtm-success/30 rounded-xl p-4"><p className="text-xl font-bold text-amtm-text">{summary.items_created}</p><p className="text-xs text-amtm-textMuted">New items</p></div>
              <div className="bg-amtm-success/10 border border-amtm-success/30 rounded-xl p-4"><p className="text-xl font-bold text-amtm-text">{summary.batches_created}</p><p className="text-xs text-amtm-textMuted">Batches created</p></div>
              <div className="bg-amtm-warning/10 border border-amtm-warning/30 rounded-xl p-4"><p className="text-xl font-bold text-amtm-text">{summary.skipped}</p><p className="text-xs text-amtm-textMuted">Skipped</p></div>
            </div>
            {summary.skipped_rows?.length > 0 && (
              <div className="bg-amtm-warning/10 border border-amtm-warning/30 rounded-xl p-3 mb-4" data-testid="import-skipped-rows-list">
                <p className="text-sm font-medium text-amtm-text mb-1">Rows not imported:</p>
                {summary.skipped_rows.map((r, i) => (
                  <p key={i} className="text-xs text-amtm-textMuted" data-testid={`import-skipped-row-${i}`}>{r.sheet_name}, row {r.row_number} — {r.reason}</p>
                ))}
              </div>
            )}
            <Link to="/admin/inventory/import-history" className="text-sm text-amtm-teal font-medium" data-testid="import-summary-history-link">
              Made a mistake? Undo this import from Import History →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
